import type {
  ProviderPricingJson,
  SkincareProductInfo,
} from "./treatmentPricing2025";
import {
  getGravitasShopListingForCatalogProduct,
} from "./gravitasShopCatalog";

type GravitasGemstoneId =
  | "opal"
  | "pearl"
  | "jade"
  | "quartz"
  | "amber"
  | "moonstone"
  | "turquoise"
  | "diamond";

export type { GravitasGemstoneId };

/** 3-letter quiz code (hydration + reactivity + pigmentation) for each gemstone skin type. */
const GRAVITAS_GEMSTONE_PROFILE_CODE: Record<GravitasGemstoneId, string> = {
  opal: "OSP",
  pearl: "OSN",
  jade: "ORP",
  quartz: "ORN",
  amber: "DSP",
  moonstone: "DSN",
  turquoise: "DRP",
  diamond: "DRN",
};

function isGravitasGemstoneId(value: string): value is GravitasGemstoneId {
  return value in GRAVITAS_GEMSTONE_PROFILE_CODE;
}

export const GRAVITAS_PROVIDER_MATCH = "gravitas";

export function isGravitasProviderCode(
  providerCode?: string | null,
  providerName?: string | null,
): boolean {
  return [providerCode, providerName].some((value) =>
    (value ?? "").toLowerCase().includes(GRAVITAS_PROVIDER_MATCH),
  );
}

export type GravitasQuizRegimenKind = "clear" | "acne";

export function getGravitasQuizRegimenKindForSkinType(
  skinType?: string | null,
): GravitasQuizRegimenKind {
  const key = (skinType ?? "").trim().toLowerCase();
  return key === "opal" || key === "pearl" || key === "jade" || key === "quartz"
    ? "acne"
    : "clear";
}

export interface GravitasSkincareProduct {
  name: string;
  price: number;
  description?: string;
  productUrl?: string;
  brand?: string;
  imageUrl?: string;
  orderNote?: string;
}

export interface GravitasRoutineStep {
  label: string;
  productNames: string[];
}

export interface GravitasRoutineProtocol {
  protocolTitle: string;
  am: GravitasRoutineStep[];
  pmWeekdays: GravitasRoutineStep[];
  pmMonThurs: GravitasRoutineStep[];
  postPeel: { am: GravitasRoutineStep[]; pm: GravitasRoutineStep[] };
  emergency: { am: GravitasRoutineStep[]; pm: GravitasRoutineStep[] };
  pleaseNote: string[];
}

const OLIVIA_QUIDO_ORDER_NOTE =
  "Gravitas Medspa is an authorized Olivia Quido reseller. Please call the clinic to order this product.";

/** Sourced from Gravitas Services and Skincare Products 2026 spreadsheets. */
const GRAVITAS_SKINCARE_PRODUCT_ROWS: GravitasSkincareProduct[] = [
  {
    name: "Skin Vitalizing Cleanser",
    price: 34,
    brand: "Gravitas",
    description:
      "Hydrating sulfate-free daily cleanser with botanical oils to cleanse and revitalize sensitive skin.",
  },
  {
    name: "Skin Vitalizing Toner",
    price: 34,
    brand: "Gravitas",
    description:
      "Botanically rich pH-balanced toner that cleans, preps, and hydrates while softening fine lines.",
  },
  {
    name: "Luminous Oil Cleanser",
    price: 34,
    brand: "Gravitas",
    description:
      "Oil cleanser that removes makeup and sunscreen while supporting a balanced skin microbiome.",
  },
  {
    name: "Oil Control Cleanser",
    price: 47,
    brand: "Gravitas",
    description:
      "Salicylic acid cleanser that penetrates pores to reduce breakouts without over-drying.",
  },
  {
    name: "Oil Control Toner",
    price: 36,
    brand: "Gravitas",
    description:
      "Salicylic acid and papaya enzyme toner that exfoliates, controls oil, and refines pores.",
  },
  {
    name: "Powerhouse Vit. C Plus",
    price: 110,
    brand: "Gravitas",
    description:
      "Potent 15% L-ascorbic acid serum with ferulic acid and vitamin E for brightening and antioxidant protection.",
  },
  {
    name: "Biome Balance",
    price: 85,
    brand: "Gravitas",
    description:
      "Hydrating serum with niacinamide and hyaluronic acid that supports the skin barrier and microbiome.",
  },
  {
    name: "Glass Skin Serum",
    price: 95,
    brand: "Gravitas",
    description:
      "Niacinamide and vitamin C serum that refines pores, brightens tone, and smooths texture.",
  },
  {
    name: "Cell Repair w/ Growth Factor",
    price: 145,
    brand: "Gravitas",
    description:
      "Growth factor and peptide serum that boosts collagen, elastin, and overall skin rejuvenation.",
  },
  {
    name: "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)",
    price: 140,
    brand: "Gravitas",
    description:
      "Triple-action retinoid and bakuchiol serum for fine lines, pores, dullness, and loss of firmness.",
  },
  {
    name: "Exolux Regenerative Cream",
    price: 145,
    brand: "Gravitas",
    description:
      "Post-procedure recovery cream with exosomes and peptides to calm, hydrate, and support healing.",
  },
  {
    name: "UV Protect SPF 44 w/ Tint and Hyaluronic Acid",
    price: 70,
    brand: "Gravitas",
    description:
      "Tinted mineral SPF 44 with hyaluronic acid — water-resistant daily sun protection.",
  },
  {
    name: "Matte Tinted Zinc UV Defense",
    price: 70,
    brand: "Gravitas",
    description:
      "Matte mineral SPF with blue-light protection and a sheer universal tint for shine control.",
  },
  {
    name: "Zinc UV Defense",
    price: 65,
    brand: "Gravitas",
    description:
      "Hydrating broad-spectrum mineral sunscreen with a silky, smooth daily-wear finish.",
  },
  {
    name: "Freshlook Eye Repair Cream",
    price: 85,
    brand: "Gravitas",
    description:
      "All-in-one eye cream with retinoid, peptides, and vitamin K for dark circles and fine lines.",
  },
  {
    name: "Blemish Corrector",
    price: 120,
    brand: "Gravitas",
    description:
      "Hydroquinone-free brightening treatment for dark spots, discoloration, and uneven tone.",
  },
  {
    name: "Acne Erase Cream",
    price: 50,
    brand: "Gravitas",
    description:
      "Micronized benzoyl peroxide and glycolic acid spot treatment that fights acne-causing bacteria.",
  },
  {
    name: "Pumice Scrub",
    price: 60,
    brand: "Gravitas",
    description:
      "Microdermabrasion-style scrub with pumice granules to smooth texture and reduce hyperpigmentation.",
  },
  {
    name: "Sulfur Mask",
    price: 70,
    brand: "Gravitas",
    description:
      "Clarifying mask with sulfur, charcoal, and kaolin to absorb oil and fight breakouts.",
  },
  {
    name: "Pumpkin Renewal Mask",
    price: 70,
    brand: "Gravitas",
    description:
      "Self-warming pumpkin enzyme mask that cleanses, hydrates, and refines pores.",
  },
  {
    name: "Youth Serum Elixir",
    price: 95,
    brand: "Cleopatra",
    productUrl: "https://essenceofcleopatra.com/products/youth-serum-elixir-1",
    description:
      "Luxurious botanical oil elixir with bakuchiol to hydrate, firm, and restore a youthful glow.",
  },
  {
    name: "Cleansing Milk",
    price: 38,
    brand: "Olivia Quido",
    productUrl: "https://oliviaquido.com/products/cleansing-milk",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Ultra-gentle hydrating cleanser that lifts impurities while supporting radiance and a soft finish.",
  },
  {
    name: "Balancing Toner",
    price: 38,
    brand: "Olivia Quido",
    productUrl: "https://oliviaquido.com/products/balancing-toner",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Alcohol-free toner that refreshes, balances pH, hydrates, and preps skin for the rest of the regimen.",
  },
  {
    name: "Active Moisturizer",
    price: 68,
    brand: "Olivia Quido",
    productUrl: "https://oliviaquido.com/products/active-moisturizer",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Multi-action moisturizer designed to resurface, brighten, and boost hydration for a glowing finish.",
  },
  {
    name: "Vitamin C Serum 20%",
    price: 120,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/lack-of-glow/products/vitamin-c-serum-20",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "High-dose vitamin C serum for brighter, tighter, and more even-looking skin.",
  },
  {
    name: "BHA Exfoliating Essence 2% Salicylic Acid + Niacinamide Solution",
    price: 48,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/lack-of-glow/products/bha-exfoliating-essence",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Leave-on BHA exfoliant with salicylic acid and niacinamide for clarity, pores, and radiance.",
  },
  {
    name: "Clarifying Shampoo",
    price: 25,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/blemish-prone/products/healthy-hair-shampoo",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Gentle hydrating shampoo, formerly Healthy Hair Shampoo, for normal to oily skin and scalp types.",
  },
  {
    name: "Healthy Hair Conditioner",
    price: 25,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/blemish-prone/products/healthy-hair-conditioner",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Restorative conditioner with keratin, wheat protein, and nourishing botanicals for soft luminous hair.",
  },
  {
    name: "Broad Spectrum SPF 50",
    price: 68,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/daily-essentials/products/broad-spectrum-sunscreen",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Hybrid SPF 50 sunscreen with zinc oxide and octinoxate for daily UVA/UVB protection.",
  },
  {
    name: "Beauty Oil",
    price: 120,
    brand: "Olivia Quido",
    productUrl: "https://oliviaquido.com/products/beauty-oil",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Lightweight beauty oil blended to absorb quickly with a soft, silky finish.",
  },
  {
    name: "Secret Pearl",
    price: 180,
    brand: "Olivia Quido",
    productUrl: "https://oliviaquido.com/products/secret-pearl",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Brightening anti-aging complex formulated to hydrate, illuminate, and target visible aging.",
  },
  {
    name: "Secret Radiance",
    price: 120,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/lack-of-glow/products/secret-radiance",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Night brightening cream that targets pigment, dullness, and visible signs of aging.",
  },
  {
    name: "Secret Gold Overnight Mask",
    price: 140,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/lack-of-glow/products/secret-gold-24k-mask",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Multi-benefit overnight mask designed to smooth and gently resurface while brightening.",
  },
  {
    name: "Secret Glow Overnight Mask",
    price: 120,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/lack-of-glow/products/secret-glow",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Overnight treatment mask designed to visibly brighten and refresh the skin by morning.",
  },
  {
    name: "Body BB Glow Brightening Cream",
    price: 95,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/lack-of-glow/products/body-bb-glow",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Body brightening lotion with milk protein peptide to moisturize, nourish, and even tone.",
  },
  {
    name: "Ultimate Glow Regimen",
    price: 980,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/lack-of-glow/products/ultimate-glow-collection-2024",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Olivia Quido regimen bundle for a comprehensive brightening and glow-focused routine.",
  },
  {
    name: "Daily Essentials Regimen",
    price: 212,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/daily-essentials/products/daily-essentials-regimen",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Daily essentials regimen designed to cleanse, tone, moisturize, and support the skin barrier.",
  },
  {
    name: "The Secret Collection",
    price: 560,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/lack-of-glow/products/secret-collection-bundle",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Layered Secret Collection bundle for youth-boosting and spot-focused results at home.",
  },
  {
    name: "Millk Brightening Body Bar",
    price: 20,
    brand: "Olivia Quido",
    productUrl:
      "https://oliviaquido.com/collections/lack-of-glow/products/milk-brightening-b",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Brightening body cleansing bar with soothing botanicals for uneven body tone.",
  },
  {
    name: "At Home Rx Peel",
    price: 0,
    brand: "Olivia Quido",
    productUrl: "https://oliviaquido.com/pages/at-home-peel",
    orderNote: OLIVIA_QUIDO_ORDER_NOTE,
    description:
      "Prescription at-home peel treatment; consultation and clinic ordering are required.",
  },
  {
    name: "Acne Blend",
    price: 65,
    brand: "Cleopatra",
    productUrl: "https://essenceofcleopatra.com/products/acne-blend",
    description:
      "Therapeutic essential oil blend that balances oil, unclogs pores, and calms breakouts.",
  },
  {
    name: "Hydrogel 6oz",
    price: 120,
    brand: "Primocyn",
    productUrl:
      "https://primocyn.com/collections/all/products/primocyn-hydrogel-6oz-170g?variant=45835173429405",
    description:
      "Advanced wound and skin care hydrogel safe for use around eyes, ears, and mouth.",
  },
  {
    name: "Primocyn Solution 236ml",
    price: 60,
    brand: "Primocyn",
    productUrl:
      "https://primocyn.com/collections/all/products/copy-of-primocyn-skin-solution-236ml?variant=36661912305821",
    description:
      "Antimicrobial skin solution for wound debridement and post-treatment calming support.",
  },
];

export const GRAVITAS_SKINCARE_PRODUCTS: GravitasSkincareProduct[] =
  GRAVITAS_SKINCARE_PRODUCT_ROWS.map((product) => {
    const shop = getGravitasShopListingForCatalogProduct(product.name);
    return {
      ...product,
      description:
        [product.description ?? "Gravitas skincare product", product.orderNote]
          .filter(Boolean)
          .join(" "),
      /** Patient-facing links only point at gravitasmedspa.com — never third-party boutiques. */
      productUrl:
        product.brand === "Olivia Quido" ? undefined : shop?.productUrl,
      imageUrl: shop?.imageUrl,
    };
  });

const productByName = new Map(
  GRAVITAS_SKINCARE_PRODUCTS.map((product) => [
    product.name.toLowerCase(),
    product,
  ]),
);

export function isGravitasClinicOnlySkincareProduct(
  productName: string,
): boolean {
  const key = productName.trim().toLowerCase();
  if (!key || key === "other") return false;
  const product =
    productByName.get(key) ??
    GRAVITAS_SKINCARE_PRODUCTS.find((p) => {
      const candidate = p.name.toLowerCase();
      return candidate.includes(key) || key.includes(candidate);
    });
  return product?.brand === "Olivia Quido";
}

export function getGravitasSkincareProductInfo(
  productName: string,
): SkincareProductInfo | null {
  const key = productName.trim().toLowerCase();
  if (!key || key === "other") return null;
  const product =
    productByName.get(key) ??
    GRAVITAS_SKINCARE_PRODUCTS.find((p) => {
      const candidate = p.name.toLowerCase();
      return candidate.includes(key) || key.includes(candidate);
    });
  if (!product) return null;
  const hasPrice = product.price > 0;
  return {
    price: hasPrice ? product.price : undefined,
    displayPrice: hasPrice ? `$${product.price}` : "Contact for pricing",
    productLabel: product.name,
    description: product.description,
  };
}

export function getGravitasSkincareCarouselItems() {
  return [
    ...GRAVITAS_SKINCARE_PRODUCTS.map((product) => ({
      name: product.name,
      imageUrl: product.imageUrl,
      description: product.description,
      price: product.price > 0 ? `$${product.price}` : "Contact for pricing",
      productUrl: product.productUrl,
    })),
    { name: "Other" },
  ];
}

export const GRAVITAS_CLEAR_SKIN_PRODUCTS = [
  "Skin Vitalizing Cleanser",
  "Skin Vitalizing Toner",
  "Powerhouse Vit. C Plus",
  "Youth Serum Elixir",
  "Biome Balance",
  "Glass Skin Serum",
  "Freshlook Eye Repair Cream",
  "Blemish Corrector",
  "Cell Repair w/ Growth Factor",
  "UV Protect SPF 44 w/ Tint and Hyaluronic Acid",
  "Luminous Oil Cleanser",
  "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)",
  "Exolux Regenerative Cream",
  "Pumpkin Renewal Mask",
];

export const GRAVITAS_ACNE_SKIN_PRODUCTS = [
  "Oil Control Cleanser",
  "Oil Control Toner",
  "Biome Balance",
  "Youth Serum Elixir",
  "Freshlook Eye Repair Cream",
  "Blemish Corrector",
  "Zinc UV Defense",
  "Primocyn Solution 236ml",
  "Luminous Oil Cleanser",
  "Acne Blend",
  "Acne Erase Cream",
  "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)",
  "Sulfur Mask",
];

/**
 * Curated Gravitas skincare subset per gemstone quiz result.
 * Profiles: O/D = oily vs dry, S/R = sensitive vs resistant, P/N = pigmented vs clear.
 */
export const GRAVITAS_SKINCARE_BY_SKIN_TYPE: Record<GravitasGemstoneId, string[]> = {
  /** Oily, sensitive, pigmented — oil control + calming + pigment support */
  opal: [
    "Oil Control Cleanser",
    "Skin Vitalizing Cleanser",
    "Oil Control Toner",
    "Biome Balance",
    "Glass Skin Serum",
    "Blemish Corrector",
    "Acne Blend",
    "Acne Erase Cream",
    "Sulfur Mask",
    "Primocyn Solution 236ml",
    "Matte Tinted Zinc UV Defense",
    "Freshlook Eye Repair Cream",
  ],
  /** Oily, sensitive, non-pigmented — gentle acne protocol without heavy brighteners */
  pearl: [
    "Oil Control Cleanser",
    "Skin Vitalizing Cleanser",
    "Oil Control Toner",
    "Biome Balance",
    "Glass Skin Serum",
    "Acne Blend",
    "Acne Erase Cream",
    "Sulfur Mask",
    "Primocyn Solution 236ml",
    "Zinc UV Defense",
    "Freshlook Eye Repair Cream",
  ],
  /** Oily, resistant, pigmented — clarifying + brightening + renewal */
  jade: [
    "Oil Control Cleanser",
    "Skin Vitalizing Cleanser",
    "Oil Control Toner",
    "Powerhouse Vit. C Plus",
    "Glass Skin Serum",
    "Blemish Corrector",
    "Biome Balance",
    "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)",
    "Cell Repair w/ Growth Factor",
    "UV Protect SPF 44 w/ Tint and Hyaluronic Acid",
    "Pumice Scrub",
  ],
  /** Oily, resistant, clear — maintenance, texture, and anti-aging */
  quartz: [
    "Oil Control Cleanser",
    "Skin Vitalizing Toner",
    "Powerhouse Vit. C Plus",
    "Glass Skin Serum",
    "Biome Balance",
    "Cell Repair w/ Growth Factor",
    "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)",
    "Exolux Regenerative Cream",
    "Matte Tinted Zinc UV Defense",
    "Pumice Scrub",
    "Pumpkin Renewal Mask",
    "Freshlook Eye Repair Cream",
  ],
  /** Dry, sensitive, pigmented — gentle cleanse + barrier + brightening */
  amber: [
    "Skin Vitalizing Cleanser",
    "Skin Vitalizing Toner",
    "Powerhouse Vit. C Plus",
    "Blemish Corrector",
    "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)",
    "Biome Balance",
    "Youth Serum Elixir",
    "Exolux Regenerative Cream",
    "UV Protect SPF 44 w/ Tint and Hyaluronic Acid",
    "Hydrogel 6oz",
  ],
  /** Dry, sensitive, clear — barrier repair and gentle hydration */
  moonstone: [
    "Skin Vitalizing Cleanser",
    "Skin Vitalizing Toner",
    "Biome Balance",
    "Youth Serum Elixir",
    "Exolux Regenerative Cream",
    "Hydrogel 6oz",
    "Zinc UV Defense",
    "Freshlook Eye Repair Cream",
  ],
  /** Dry, resistant, pigmented — brightening, collagen, and renewal */
  turquoise: [
    "Skin Vitalizing Cleanser",
    "Luminous Oil Cleanser",
    "Skin Vitalizing Toner",
    "Powerhouse Vit. C Plus",
    "Blemish Corrector",
    "Cell Repair w/ Growth Factor",
    "Biome Balance",
    "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)",
    "UV Protect SPF 44 w/ Tint and Hyaluronic Acid",
    "Pumpkin Renewal Mask",
    "Pumice Scrub",
  ],
  /** Dry, resistant, clear — premium rejuvenation and glow */
  diamond: [
    "Luminous Oil Cleanser",
    "Skin Vitalizing Cleanser",
    "Skin Vitalizing Toner",
    "Powerhouse Vit. C Plus",
    "Cell Repair w/ Growth Factor",
    "Glass Skin Serum",
    "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)",
    "Exolux Regenerative Cream",
    "UV Protect SPF 44 w/ Tint and Hyaluronic Acid",
    "Pumpkin Renewal Mask",
    "Freshlook Eye Repair Cream",
  ],
};

function dedupeGravitasCatalogProductNames(names: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names) {
    const name = raw.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    if (!productByName.has(key)) continue;
    seen.add(key);
    out.push(productByName.get(key)!.name);
  }
  return out;
}

export function getGravitasRecommendedProductsForSkinType(
  skinType?: string | null,
): string[] {
  const key = (skinType ?? "").trim().toLowerCase();
  if (isGravitasGemstoneId(key)) {
    return dedupeGravitasCatalogProductNames(GRAVITAS_SKINCARE_BY_SKIN_TYPE[key]);
  }
  return getGravitasQuizRegimenKindForSkinType(skinType) === "acne"
    ? dedupeGravitasCatalogProductNames(GRAVITAS_ACNE_SKIN_PRODUCTS)
    : dedupeGravitasCatalogProductNames(GRAVITAS_CLEAR_SKIN_PRODUCTS);
}

/** Full Clear Skin Guide and Acne Erase Protocol regimens from Gravitas in-clinic handouts. */
export const GRAVITAS_ROUTINE_BY_KIND: Record<
  GravitasQuizRegimenKind,
  GravitasRoutineProtocol
> = {
  clear: {
    protocolTitle: "Clear Skin Guide",
    am: [
      {
        label: "Cleanse",
        productNames: [
          "Skin Vitalizing Cleanser",
          "Oil Control Cleanser",
        ],
      },
      {
        label: "Vitamin C serum",
        productNames: ["Powerhouse Vit. C Plus"],
      },
      {
        label: "Serums & correctors",
        productNames: [
          "Youth Serum Elixir",
          "Biome Balance",
          "Blemish Corrector",
          "Cell Repair w/ Growth Factor",
        ],
      },
      {
        label: "Spot treatment (breakouts only)",
        productNames: ["Acne Blend"],
      },
      { label: "Eye cream", productNames: ["Freshlook Eye Repair Cream"] },
      {
        label: "SPF",
        productNames: [
          "UV Protect SPF 44 w/ Tint and Hyaluronic Acid",
          "Zinc UV Defense",
        ],
      },
      {
        label: "As needed",
        productNames: ["Primocyn Solution 236ml", "Skin Vitalizing Toner"],
      },
    ],
    pmWeekdays: [
      {
        label: "Double cleanse",
        productNames: [
          "Luminous Oil Cleanser",
          "Skin Vitalizing Cleanser",
          "Oil Control Cleanser",
        ],
      },
      { label: "Eye cream", productNames: ["Freshlook Eye Repair Cream"] },
      {
        label: "Serums & correctors",
        productNames: [
          "Youth Serum Elixir",
          "Blemish Corrector",
          "Biome Balance",
        ],
      },
      {
        label: "Spot treatment (breakouts only)",
        productNames: ["Acne Blend"],
      },
      {
        label: "Retinol (pause before facials)",
        productNames: ["Silky Smooth Plus (Retinol w/ Bakuchiol Serum)"],
      },
    ],
    pmMonThurs: [
      {
        label: "Cleanse",
        productNames: ["Luminous Oil Cleanser", "Skin Vitalizing Cleanser"],
      },
      {
        label: "Exfoliate (alternate mask weekly)",
        productNames: ["Pumice Scrub", "Pumpkin Renewal Mask"],
      },
      {
        label: "Serum",
        productNames: ["Biome Balance", "Youth Serum Elixir"],
      },
      {
        label: "Overnight treatment",
        productNames: ["Blemish Corrector", "Pumpkin Renewal Mask"],
      },
    ],
    postPeel: {
      am: [
        {
          label: "Gentle cleanse",
          productNames: ["Skin Vitalizing Cleanser", "Luminous Oil Cleanser"],
        },
        {
          label: "Moisturize",
          productNames: ["Exolux Regenerative Cream"],
        },
        {
          label: "SPF",
          productNames: [
            "UV Protect SPF 44 w/ Tint and Hyaluronic Acid",
            "UV Protect SPF 44 w/ Tint and Hyaluronic Acid",
          ],
        },
      ],
      pm: [
        {
          label: "Gentle cleanse",
          productNames: ["Skin Vitalizing Cleanser", "Luminous Oil Cleanser"],
        },
        {
          label: "Moisturize",
          productNames: ["Exolux Regenerative Cream"],
        },
        {
          label: "Spot treatment",
          productNames: ["Acne Erase Cream"],
        },
      ],
    },
    emergency: {
      am: [
        { label: "Gentle cleanse", productNames: ["Skin Vitalizing Cleanser"] },
        { label: "Calming hydrogel", productNames: ["Hydrogel 6oz"] },
        {
          label: "Moisturizer",
          productNames: ["Exolux Regenerative Cream"],
        },
        {
          label: "SPF",
          productNames: ["Zinc UV Defense", "UV Protect SPF 44 w/ Tint and Hyaluronic Acid"],
        },
      ],
      pm: [
        { label: "Gentle cleanse", productNames: ["Skin Vitalizing Cleanser"] },
        { label: "Calming hydrogel", productNames: ["Hydrogel 6oz"] },
        {
          label: "Moisturizer",
          productNames: ["Exolux Regenerative Cream"],
        },
      ],
    },
    pleaseNote: [
      "After peels, expect redness and peeling — avoid waxing, heat, and sun until skin heals.",
      "Stop retinol several days before your next appointment.",
      "Send front, right, and left progress photos in good lighting during peeling.",
    ],
  },
  acne: {
    protocolTitle: "Acne Erase Protocol",
    am: [
      {
        label: "Cleanse",
        productNames: [
          "Oil Control Cleanser",
          "Skin Vitalizing Cleanser",
          "Luminous Oil Cleanser",
        ],
      },
      { label: "Toner", productNames: ["Oil Control Toner"] },
      {
        label: "Serums & correctors",
        productNames: [
          "Biome Balance",
          "Youth Serum Elixir",
          "Blemish Corrector",
        ],
      },
      { label: "Eye cream", productNames: ["Freshlook Eye Repair Cream"] },
      {
        label: "SPF (reapply every 2 hours)",
        productNames: ["Zinc UV Defense", "Matte Tinted Zinc UV Defense"],
      },
      {
        label: "As needed",
        productNames: ["Primocyn Solution 236ml"],
      },
    ],
    pmWeekdays: [
      {
        label: "Double cleanse",
        productNames: [
          "Luminous Oil Cleanser",
          "Oil Control Cleanser",
          "Skin Vitalizing Cleanser",
        ],
      },
      { label: "Toner", productNames: ["Oil Control Toner"] },
      {
        label: "Treatments",
        productNames: [
          "Acne Blend",
          "Biome Balance",
          "Blemish Corrector",
          "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)",
        ],
      },
      { label: "Eye cream", productNames: ["Freshlook Eye Repair Cream"] },
      {
        label: "Spot treatment",
        productNames: ["Acne Erase Cream"],
      },
      { label: "Primocyn spray", productNames: ["Primocyn Solution 236ml"] },
    ],
    pmMonThurs: [
      {
        label: "Cleanse",
        productNames: [
          "Luminous Oil Cleanser",
          "Oil Control Cleanser",
          "Skin Vitalizing Cleanser",
        ],
      },
      { label: "Clarifying mask", productNames: ["Sulfur Mask"] },
      { label: "Serum", productNames: ["Biome Balance"] },
      { label: "Overnight treatment", productNames: ["Pumpkin Renewal Mask"] },
    ],
    postPeel: {
      am: [
        {
          label: "Gentle cleanse",
          productNames: ["Skin Vitalizing Cleanser", "Luminous Oil Cleanser"],
        },
        {
          label: "Moisturize",
          productNames: ["Exolux Regenerative Cream", "Biome Balance"],
        },
        {
          label: "SPF",
          productNames: ["Zinc UV Defense", "Matte Tinted Zinc UV Defense"],
        },
      ],
      pm: [
        {
          label: "Gentle cleanse",
          productNames: ["Skin Vitalizing Cleanser", "Luminous Oil Cleanser"],
        },
        {
          label: "Moisturize",
          productNames: ["Exolux Regenerative Cream", "Biome Balance"],
        },
        {
          label: "Spot treatment",
          productNames: ["Acne Erase Cream"],
        },
      ],
    },
    emergency: {
      am: [
        { label: "Gentle cleanse", productNames: ["Skin Vitalizing Cleanser"] },
        { label: "Calming hydrogel", productNames: ["Hydrogel 6oz"] },
        {
          label: "Moisturizer",
          productNames: ["Exolux Regenerative Cream"],
        },
        {
          label: "SPF",
          productNames: ["Zinc UV Defense", "UV Protect SPF 44 w/ Tint and Hyaluronic Acid"],
        },
      ],
      pm: [
        { label: "Gentle cleanse", productNames: ["Skin Vitalizing Cleanser"] },
        { label: "Calming hydrogel", productNames: ["Hydrogel 6oz"] },
        {
          label: "Moisturizer",
          productNames: ["Exolux Regenerative Cream"],
        },
      ],
    },
    pleaseNote: [
      "After peels, expect redness and peeling — avoid waxing, heat, and sun until skin heals.",
      "Stop retinol several days before your next appointment.",
      "Send front, right, and left progress photos in good lighting during peeling.",
    ],
  },
};

export function getGravitasRoutineForSkinType(skinType?: string | null) {
  return GRAVITAS_ROUTINE_BY_KIND[getGravitasQuizRegimenKindForSkinType(skinType)];
}

export function getGravitasProtocolProducts(
  kind: GravitasQuizRegimenKind,
): string[] {
  const protocol = GRAVITAS_ROUTINE_BY_KIND[kind];
  const names = new Set<string>();
  const collect = (steps: GravitasRoutineStep[]) => {
    for (const step of steps) {
      for (const name of step.productNames) names.add(name);
    }
  };
  collect(protocol.am);
  collect(protocol.pmWeekdays);
  collect(protocol.pmMonThurs);
  collect(protocol.postPeel.am);
  collect(protocol.postPeel.pm);
  collect(protocol.emergency.am);
  collect(protocol.emergency.pm);
  return [...names];
}

function uniqueGravitasProductNames(names: string[]): string[] {
  return [...new Set(names)];
}

function buildGravitasSpecialSituationSteps(
  protocol: GravitasRoutineProtocol,
): GravitasRoutineStep[] {
  return [
    {
      label: "After a peel — morning",
      productNames: uniqueGravitasProductNames(
        protocol.postPeel.am.flatMap((step) => step.productNames),
      ),
    },
    {
      label: "After a peel — evening",
      productNames: uniqueGravitasProductNames(
        protocol.postPeel.pm.flatMap((step) => step.productNames),
      ),
    },
    {
      label: "If irritated — morning (5–7 days)",
      productNames: uniqueGravitasProductNames(
        protocol.emergency.am.flatMap((step) => step.productNames),
      ),
    },
    {
      label: "If irritated — evening (5–7 days)",
      productNames: uniqueGravitasProductNames(
        protocol.emergency.pm.flatMap((step) => step.productNames),
      ),
    },
  ];
}

function formatGravitasRoutineSteps(steps: GravitasRoutineStep[]): string {
  return steps
    .map((step, index) => {
      const products =
        step.productNames.length > 0 ? ` (${step.productNames.join(" OR ")})` : "";
      return `${index + 1}. ${step.label}${products}`;
    })
    .join("\n");
}

export function formatGravitasProtocolInstructionsText(
  kind: GravitasQuizRegimenKind,
): string {
  const protocol = GRAVITAS_ROUTINE_BY_KIND[kind];
  const notes = protocol.pleaseNote.map((note) => `• ${note}`).join("\n");
  const special = buildGravitasSpecialSituationSteps(protocol);
  return `${protocol.protocolTitle}

MORNING (daily)
${formatGravitasRoutineSteps(protocol.am)}

EVENING — most nights (Tue–Sun)
${formatGravitasRoutineSteps(protocol.pmWeekdays)}

EVENING — weekly (Mon & Thu)
${formatGravitasRoutineSteps(protocol.pmMonThurs)}

AFTER A PEEL OR IF IRRITATED
${formatGravitasRoutineSteps(special)}

PLEASE NOTE
${notes}`;
}

export interface GravitasRoutineDisplaySection {
  id: string;
  title: string;
  steps: GravitasRoutineStep[];
}

export function getGravitasRoutineDisplaySections(
  kind: GravitasQuizRegimenKind,
): {
  protocolTitle: string;
  pleaseNote: string[];
  sections: GravitasRoutineDisplaySection[];
} {
  const protocol = GRAVITAS_ROUTINE_BY_KIND[kind];
  return {
    protocolTitle: protocol.protocolTitle,
    pleaseNote: [...protocol.pleaseNote],
    sections: [
      { id: "am", title: "Morning", steps: protocol.am },
      {
        id: "pm-weekdays",
        title: "Evening (most nights)",
        steps: protocol.pmWeekdays,
      },
      {
        id: "pm-mon-thurs",
        title: "Weekly evening (Mon & Thu)",
        steps: protocol.pmMonThurs,
      },
      {
        id: "special",
        title: "After a peel or if irritated",
        steps: buildGravitasSpecialSituationSteps(protocol),
      },
    ],
  };
}

export const GRAVITAS_RECOMMENDED_PRODUCT_REASONS: Record<string, string> = {
  "Skin Vitalizing Cleanser": "Daily cleanse",
  "Skin Vitalizing Toner": "Tone and prep",
  "Powerhouse Vit. C Plus": "Antioxidant brightening",
  "Youth Serum Elixir": "Serum support",
  "Biome Balance": "Barrier balance",
  "Glass Skin Serum": "Pore refining and brightening",
  "Freshlook Eye Repair Cream": "Eye area support",
  "Blemish Corrector": "Blemish support",
  "Cell Repair w/ Growth Factor": "Repair and renewal",
  "UV Protect SPF 44 w/ Tint and Hyaluronic Acid": "Daily sun protection",
  "Matte Tinted Zinc UV Defense": "Shine-control daily SPF",
  "Luminous Oil Cleanser": "Makeup and SPF removal",
  "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)": "Retinol renewal",
  "Exolux Regenerative Cream": "Night repair",
  "Pumpkin Renewal Mask": "Renewal mask",
  "Pumice Scrub": "Weekly exfoliation",
  "Oil Control Cleanser": "Oil control cleanse",
  "Oil Control Toner": "Oil control tone",
  "Zinc UV Defense": "Daily sun protection",
  "Primocyn Solution 236ml": "Calming support",
  "Hydrogel 6oz": "Post-treatment skin support",
  "Acne Blend": "Acne-prone skin support",
  "Sulfur Mask": "Clarifying mask",
  "Acne Erase Cream": "Spot-treatment support",
};

/** In-spa treatment recommendations shown on Gravitas skin quiz results. */
export const GRAVITAS_TREATMENT_RECOMMENDATIONS_BY_SKIN_TYPE: Record<
  GravitasGemstoneId,
  { heading: string; items: string[] }
> = {
  opal: {
    heading: "Reactive, breakout-prone skin with pigmentation concerns",
    items: [
      "Acne Treatment w/ Blue LED Light Therapy — clears congestion and calms inflammation",
      "Skin Peeling — targets hyperpigmentation and uneven tone",
      "Glow N Go Facial — brightening maintenance facial",
      "Skinpen Microneedling — improves texture and acne scarring",
    ],
  },
  pearl: {
    heading: "Reactive, breakout-prone skin without heavy pigmentation",
    items: [
      "Acne Treatment w/ Blue LED Light Therapy — blue light and extractions for clearer skin",
      "PRP Microneedling — collagen support and scar improvement",
      "Skin Vitalizing Experience — customized hydration and relaxation",
      "LED Light Therapy add-on — supports recovery and radiance",
    ],
  },
  jade: {
    heading: "Resilient skin with sun damage and pigmentation",
    items: [
      "Skin Peeling — depigmentation and brightening treatment",
      "Ultimate Skin Veneer Experience — detoxifies, tightens, and brightens",
      "Skinpen Microneedling — collagen renewal and texture refinement",
      "Radiance Infusion Express — pre-event oxygenating facial glow",
    ],
  },
  quartz: {
    heading: "Balanced, clear skin — ideal for advanced rejuvenation",
    items: [
      "Ultimate Oxylux Experience — OxyGeneo exfoliation, infusion, and massage",
      "Skinpen Microneedling — overall skin renewal",
      "Botox — softens expression lines with natural results",
      "Red Light Therapy — collagen stimulation and whole-body wellness",
    ],
  },
  amber: {
    heading: "Delicate, dry skin with discoloration",
    items: [
      "Skin Vitalizing Experience — brightening facial with hand and foot paraffin",
      "Skin Peeling — gentle pigment correction",
      "Radiant Neck Reset — 3-in-1 neck rejuvenation treatment",
      "Gluta C IV Therapy — antioxidant brightening from within",
    ],
  },
  moonstone: {
    heading: "Sensitive, dry skin that needs gentle care",
    items: [
      "Glow N Go Facial — deep cleanse, massage, and biocellulose mask",
      "Skin Vitalizing Experience — customized hydration and relaxation",
      "Rejuvenating Pumpkin Spice Experience w/ Head Massage — seasonal renewal",
      "Hydration Renewal Drip — replenishes fluids and supports recovery",
    ],
  },
  turquoise: {
    heading: "Resilient skin with pigmentation and aging concerns",
    items: [
      "Ultimate Skin Veneer Experience — instant hydration, tightening, and brightening",
      "Skin Peeling — melasma and hyperpigmentation support",
      "PRP Microneedling — regenerative collagen stimulation",
      "Fountain of Youth IV Therapy — anti-aging wellness infusion",
    ],
  },
  diamond: {
    heading: "Resilient, well-balanced skin ready for premium treatments",
    items: [
      "Ultimate Oxylux Experience — full OxyGeneo protocol with LED therapy",
      "Skinpen Microneedling — long-term collagen and radiance",
      "Botox — refined, natural anti-aging maintenance",
      "Ombre' Eyebrows Powdershading — polished, low-maintenance brows",
    ],
  },
};

export function getGravitasTreatmentRecommendationsForSkinType(
  skinType: GravitasGemstoneId,
): { heading: string; items: string[] } | undefined {
  return GRAVITAS_TREATMENT_RECOMMENDATIONS_BY_SKIN_TYPE[skinType];
}

/** Facial experiences and add-ons from the 2026 services sheet. */
export const GRAVITAS_FACIAL_SERVICE_OPTIONS = [
  "Glow N Go Facial",
  "Skin Vitalizing Experience",
  "Ultimate Skin Veneer Experience",
  "Ultimate Oxylux Experience",
  "Acne Treatment w/ Blue LED Light Therapy",
  "Rejuvenating Pumpkin Spice Experience w/ Head Massage",
  "Radiance Infusion Express",
  "Radiant Neck Reset",
  "Underarm Glow Revival",
  "LED Light Therapy",
  "Epidermal Sweep",
  "Medical Extractions",
  "Paraffin Treatment (Hands and Feet)",
  "Eyebrow Grooming",
  "Red Light Therapy",
  "FREE Birthday Skin Cleanse",
] as const;

export const GRAVITAS_IV_WELLNESS_OFFERINGS: ReadonlyArray<{
  name: string;
  description: string;
}> = [
  {
    name: "Gluta C Single Dose",
    description:
      "Gluta-C IV Therapy delivers Vitamin C (Ascorbic Acid) and Glutathione directly into the bloodstream for optimal absorption. Together they support immune health, collagen production, antioxidant protection, and natural detoxification. Clients may also notice brighter, more even skin, improved energy, enhanced recovery, and overall vitality.",
  },
  {
    name: "Gluta C - Brilliance (Package of 8)",
    description:
      "An 8-week package of weekly high-potency infusions with 5,000mg Ascorbic Acid and 2,000mg Glutathione per session. Designed for consistent wellness and skin-rejuvenating benefits: immune support, antioxidant protection, collagen support, brighter radiance, and more even tone with ongoing energy and recovery support.",
  },
  {
    name: "Fountain of Youth 5K (Vit C 5000mg)",
    description:
      "Premium anti-aging and wellness infusion with Vitamin C, Glutathione, B-Complex vitamins, Magnesium, Calcium, and amino acids for cellular nourishment, collagen support, hydration, and brighter skin. Ideal before and after travel for immune support, fatigue recovery, and a refreshed glow from within.",
  },
  {
    name: "Fountain of Youth 15K (Vit C 15,000mg)",
    description:
      "Higher-potency Fountain of Youth infusion with 15,000mg Vitamin C plus Glutathione, B-Complex vitamins, Magnesium, Calcium, and amino acids. Supports anti-aging wellness, immune defenses, hydration, travel recovery, and skin rejuvenation at the cellular level.",
  },
  {
    name: "Hydration Renewal Drip",
    description:
      "Replenishes fluids, electrolytes, and essential nutrients directly into the bloodstream for fast hydration. Supports energy, recovery, mental clarity, and immune function — ideal for dehydration, fatigue, travel, workouts, headaches, or general wellness support.",
  },
  {
    name: "Ultra C Wellness Drip",
    description:
      "High-potency Vitamin C infusion for immune health, antioxidant protection, and overall wellness. Supports collagen and brighter skin, energy, hydration, reduced fatigue, and faster recovery. Recommended before and after travel for immune support and jet-lag recovery.",
  },
  {
    name: "Lipomelt Shot",
    description:
      "The MIC B12 Shot combines Methionine, Inositol, Choline (MIC), and Vitamin B12 to support fat metabolism, energy production, and metabolic health while assisting the body's natural detoxification processes.",
  },
];

export const GRAVITAS_IV_WELLNESS_OPTIONS: string[] =
  GRAVITAS_IV_WELLNESS_OFFERINGS.map((o) => o.name);

const GRAVITAS_IV_WELLNESS_DESCRIPTION_BY_NAME = Object.fromEntries(
  GRAVITAS_IV_WELLNESS_OFFERINGS.map((o) => [o.name, o.description]),
) as Record<string, string>;

export function getGravitasIvWellnessDescription(
  productName: string | null | undefined,
): string | undefined {
  const key = (productName ?? "").trim();
  if (!key) return undefined;
  return GRAVITAS_IV_WELLNESS_DESCRIPTION_BY_NAME[key];
}

export function getGravitasIvWellnessOfferings(): ReadonlyArray<{
  name: string;
  description: string;
}> {
  return GRAVITAS_IV_WELLNESS_OFFERINGS;
}

export const GRAVITAS_PLAN_BUILDER_TREATMENTS = [
  "Skincare",
  "Facial Services",
  "Chemical Peel",
  "Microneedling",
  "Neurotoxin",
  "IV & Wellness",
  "Other procedures",
] as const;

export const GRAVITAS_PRODUCT_OPTIONS_BY_TREATMENT: Record<string, string[]> = {
  Skincare: [...GRAVITAS_SKINCARE_PRODUCTS.map((p) => p.name), "Other"],
  "Facial Services": [...GRAVITAS_FACIAL_SERVICE_OPTIONS],
  "Chemical Peel": ["Skin Peeling"],
  Microneedling: ["Skinpen Microneedling", "PRP Microneedling"],
  Neurotoxin: ["Botox"],
  "IV & Wellness": [...GRAVITAS_IV_WELLNESS_OPTIONS],
  "Other procedures": [
    "Ombre' Eyebrows Powdershading",
    "Natural Warts Removal",
    "At Home Rx Peel",
  ],
};

const CONTACT_FOR_PRICING = "Contact for pricing";

export const GRAVITAS_PRICE_LIST_2026: ProviderPricingJson = [
  {
    category: "Facial Services",
    items: [
      { name: "Glow N Go Facial", price: 165 },
      { name: "Skin Vitalizing Experience", price: 245 },
      { name: "Ultimate Skin Veneer Experience", price: 310 },
      { name: "Ultimate Oxylux Experience", price: 410 },
      { name: "Acne Treatment w/ Blue LED Light Therapy", price: 250 },
      {
        name: "Rejuvenating Pumpkin Spice Experience w/ Head Massage",
        price: 0,
        note: CONTACT_FOR_PRICING,
      },
      { name: "Radiance Infusion Express", price: 0, note: CONTACT_FOR_PRICING },
      { name: "Radiant Neck Reset", price: 0, note: CONTACT_FOR_PRICING },
      { name: "Underarm Glow Revival", price: 0, note: CONTACT_FOR_PRICING },
      { name: "LED Light Therapy", price: 0, note: CONTACT_FOR_PRICING },
      { name: "Epidermal Sweep", price: 0, note: CONTACT_FOR_PRICING },
      { name: "Medical Extractions", price: 0, note: CONTACT_FOR_PRICING },
      {
        name: "Paraffin Treatment (Hands and Feet)",
        price: 0,
        note: CONTACT_FOR_PRICING,
      },
      { name: "Eyebrow Grooming", price: 0, note: CONTACT_FOR_PRICING },
      { name: "Red Light Therapy", price: 0, note: CONTACT_FOR_PRICING },
      { name: "FREE Birthday Skin Cleanse", price: 0 },
    ],
  },
  {
    category: "Chemical Peel",
    items: [{ name: "Skin Peeling", price: 250 }],
  },
  {
    category: "Medical Spa",
    items: [
      { name: "Skinpen Microneedling", price: 450 },
      { name: "PRP Microneedling", price: 750 },
    ],
  },
  {
    category: "Injectables",
    items: [{ name: "Botox 1-Unit", price: 13, note: "$13/unit" }],
  },
  {
    category: "IV & Wellness",
    items: [
      { name: "Gluta C Single Dose", price: 0, note: CONTACT_FOR_PRICING },
      {
        name: "Gluta C - Brilliance (Package of 8)",
        price: 0,
        note: CONTACT_FOR_PRICING,
      },
      {
        name: "Fountain of Youth 5K (Vit C 5000mg)",
        price: 0,
        note: CONTACT_FOR_PRICING,
      },
      {
        name: "Fountain of Youth 15K (Vit C 15,000mg)",
        price: 0,
        note: CONTACT_FOR_PRICING,
      },
      { name: "Hydration Renewal Drip", price: 0, note: CONTACT_FOR_PRICING },
      { name: "Ultra C Wellness Drip", price: 0, note: CONTACT_FOR_PRICING },
      { name: "Lipomelt Shot", price: 0, note: CONTACT_FOR_PRICING },
    ],
  },
  {
    category: "Other Treatments",
    items: [
      { name: "Ombre' Eyebrows Powdershading", price: 799 },
      {
        name: "Natural Warts Removal",
        price: 400,
        note: "Varies from $400/area",
      },
      { name: "At Home Rx Peel", price: 0, note: "Prescription — consultation required" },
    ],
  },
];

export function getGravitasProductOptionsForTreatment(
  treatment: string,
): string[] {
  return [...(GRAVITAS_PRODUCT_OPTIONS_BY_TREATMENT[treatment] ?? [])];
}
